import { useTranslation } from 'react-i18next';
import type { Question } from '../types';
import { newQuestion } from '../data';
import { QuestionCard } from '../components/QuestionCard';
import { useBuilderContext } from '../context/BuilderContext';
import { useGdprOuKey } from '@/hooks/useOUs';

export function StepPreguntas() {
  const { t } = useTranslation();
  const { form, setForm, enfoqueFilter, setEnfoqueFilter } = useBuilderContext();
  const gdprOuKey = useGdprOuKey();
  const isEuropaOnly = form.audienceOUs.length === 1 && !!gdprOuKey && form.audienceOUs[0] === gdprOuKey;

  const hasIndividual = form.questions.some(q => q.enfoque === 'individual');
  const hasColectivo  = form.questions.some(q => q.enfoque === 'colectivo');
  const hasMixed = hasIndividual && hasColectivo;

  const countInd = form.questions.filter(q => q.enfoque === 'individual').length;
  const countCol = form.questions.filter(q => q.enfoque === 'colectivo').length;

  const activeFilter = hasMixed ? enfoqueFilter : 'all';

  const visibleQuestions = activeFilter === 'all'
    ? form.questions
    : form.questions.filter(q => q.enfoque === activeFilter);

  function addQuestion() {
    setForm(p => ({ ...p, questions: [...p.questions, newQuestion()] }));
  }

  function updateQuestion(id: string, field: keyof Question, value: unknown) {
    setForm(p => ({
      ...p,
      questions: p.questions.map(q => q.id === id ? { ...q, [field]: value } : q),
    }));
  }

  function removeQuestion(id: string) {
    setForm(p => ({ ...p, questions: p.questions.filter(q => q.id !== id) }));
  }

  return (
    <div className="builder-section">
      <div className="section-header">
        <h3><i className="fas fa-question-circle" /> {t('admin.builderQuestionsTitle')}</h3>
      </div>

      {isEuropaOnly && (
        <div className="info-banner-small europa-mode-note">
          <i className="fas fa-shield-alt" />
          <p>{t('admin.builderEuropaQuestionsNote')}</p>
        </div>
      )}
      <div className="info-banner-small" style={{ marginBottom: 12 }}>
        <i className="fas fa-info-circle" />
        <p>{t('admin.builderQuestionsApplyToAll')}</p>
      </div>

      {hasMixed && (
        <div className="qs-enfoque-tabs">
          <button
            type="button"
            className={`qs-enfoque-tab${activeFilter === 'all' ? ' active' : ''}`}
            onClick={() => setEnfoqueFilter('all')}
          >
            {t('admin.builderFilterAll')}
            <span className="qs-enfoque-count">{form.questions.length}</span>
          </button>
          <button
            type="button"
            className={`qs-enfoque-tab${activeFilter === 'individual' ? ' active' : ''}`}
            onClick={() => setEnfoqueFilter('individual')}
          >
            <i className="fas fa-user-check" /> {t('admin.builderApproachIndividual')}
            <span className="qs-enfoque-count">{countInd}</span>
          </button>
          <button
            type="button"
            className={`qs-enfoque-tab${activeFilter === 'colectivo' ? ' active' : ''}`}
            onClick={() => setEnfoqueFilter('colectivo')}
          >
            <i className="fas fa-users" /> {t('admin.builderApproachCollective')}
            <span className="qs-enfoque-count">{countCol}</span>
          </button>
        </div>
      )}

      <div id="questions-list">
        {form.questions.length === 0 ? (
          <div className="bld-empty-qs">
            <img src="/assets/persona-asomada.svg" alt="" style={{ width: 80, opacity: 0.5, marginBottom: 16 }} />
            <p>{t('admin.builderNoQuestions')}</p>
            <span>{t('admin.builderNoQuestionsHint')}</span>
          </div>
        ) : visibleQuestions.length === 0 ? (
          <div className="bld-empty-qs">
            <i className="fas fa-filter" style={{ fontSize: '2rem', color: 'var(--text-muted)', marginBottom: 12 }} />
            <p>{t('admin.builderNoQuestionsOfType')}</p>
          </div>
        ) : (
          visibleQuestions.map(q => {
            const realIndex = form.questions.findIndex(fq => fq.id === q.id);
            return (
              <QuestionCard
                key={q.id}
                q={q}
                index={realIndex}
                onChange={(field, value) => updateQuestion(q.id, field, value)}
                onRemove={() => removeQuestion(q.id)}
              />
            );
          })
        )}
      </div>

      <button type="button" className="bld-add-question-btn" onClick={addQuestion}>
        <i className="fas fa-plus-circle" /> {t('admin.builderAddQuestion')}
      </button>
    </div>
  );
}
